import {
  StoreKit,
  createGuideStatus,
  makeSelectGuideById,
  makeSelectGuideStatusById,
  selectGuide,
  selectMetadata,
  setGuideCurrentPage,
  toggleGuidesSidebar,
} from '@nx/state';
import { createAsyncThunk } from '@reduxjs/toolkit';

export type ExecutedPlayCommandPayload = {
  requestedGuideUrlOrId: string;
};

/**
 * Normalizes the guide URL or Id to create a stable ID
 */
const parseGuideId = (requestedGuideUrlOrId: string): string => {
  try {
    const url = new URL(requestedGuideUrlOrId);

    let { pathname } = url;
    pathname = pathname.replace(/\/+$/, '') || '';
    pathname = pathname.replace(/^\//, '');

    const params = new URLSearchParams([...url.searchParams.entries()].sort());
    const search = params.toString() ? `?${params.toString()}` : '';

    return `${pathname}${search}`
      .replace(/[^\w\d\s:]/g, '-')
      .toLowerCase()
      .trim();
  } catch (error) {
    // ID can be passed via url, or directly like `:play stackoverflow`. So for invalid URLs we assume it's an ID
    return requestedGuideUrlOrId;
  }
};

export const executePlayCommandThunk = createAsyncThunk<
  ExecutedPlayCommandPayload,
  ExecutedPlayCommandPayload,
  Record<string, never>
>('stream/executePlayCommand', async (payload: ExecutedPlayCommandPayload) => {
  const { requestedGuideUrlOrId } = payload;

  const { globalStore } = StoreKit.UNSAFE_getGlobalStoreInstance();
  const rootState = globalStore.getState();

  const id = parseGuideId(requestedGuideUrlOrId);

  const guide = makeSelectGuideById(id)(rootState);
  const guideStatus = makeSelectGuideStatusById(id)(rootState);
  const { guidesIsOpen } = globalStore.getState().sidebar;
  const { configuration } = selectMetadata(globalStore.getState());

  if (!guidesIsOpen) {
    globalStore.dispatch(toggleGuidesSidebar());
  }

  await globalStore.dispatch(
    selectGuide({
      id,
      title: '',
      url: requestedGuideUrlOrId,
      custom: true,

      allowedHosts: configuration?.remoteContentHostnameWhitelist,
    }),
  );

  if (guideStatus === undefined) {
    globalStore.dispatch(createGuideStatus({ id, currentPage: 0, isCompleted: false }));
  } else if (guide && guideStatus.currentPage >= guide.totalPages) {
    globalStore.dispatch(setGuideCurrentPage({ id, pageNumber: guide.totalPages - 1 }));
  }

  return { requestedGuideUrlOrId };
});
